import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { ColorService } from '../../service/color/color.service';

/** One WCAG check the card shows: "Normal Text · AA", passed or not. */
export interface ContrastCheck {
  label: string;
  level: string;
  need: number;
  pass: boolean;
}

/**
 * The Contrast Checker card (2026-10-15): a text colour and a background colour on the left, the two together
 * on the right with their WCAG 2 contrast ratio, the five WCAG checks (normal and large text at AA and AAA, user
 * interface parts at 3:1) and the APCA Lc. Every number comes from ColorService, the same maths the Color Explorer
 * shows. Used by the /tools/contrast-checker page; it holds no page state of its own beyond the two colours.
 */
@Component( {
  selector: 'app-contrast-widget',
  templateUrl: './contrast-widget.component.html',
  styleUrls: [ './contrast-widget.component.scss' ],
} )
export class ContrastWidgetComponent implements OnChanges {

  /** '#rrggbb' */
  @Input() fg = '#1A3746';
  @Input() bg = '#E4ECEF';
  @Output() colorsChange = new EventEmitter<{ fg: string; bg: string }>();

  fgText = '';
  bgText = '';
  ratio = 1;
  apca = 0;
  apcaLabel = '';
  checks: ContrastCheck[] = [];
  copied = false;
  private copiedTimer: any = null;

  constructor( private colorService: ColorService ) {
  }

  ngOnChanges(): void {
    this.fg = this.normalize( this.fg ) || '#1A3746';
    this.bg = this.normalize( this.bg ) || '#E4ECEF';
    this.fgText = this.fg.substring( 1 );
    this.bgText = this.bg.substring( 1 );
    this.measure();
  }

  /** '#rrggbb' upper case from any 6 hex digits, else '' */
  normalize( raw: string | null | undefined ): string {
    const hex = ( raw || '' ).replace( /[^0-9a-f]/gi, '' );
    return hex.length === 6 ? '#' + hex.toUpperCase() : '';
  }

  /** the native picker or a full hex in a field */
  set( which: 'fg' | 'bg', raw: string ): void {
    const hex = this.normalize( raw );
    if ( !hex ) {
      return;
    }
    this[ which ] = hex;
    if ( which === 'fg' ) {
      this.fgText = hex.substring( 1 );
    } else {
      this.bgText = hex.substring( 1 );
    }
    this.measure();
    this.colorsChange.emit( { fg: this.fg, bg: this.bg } );
  }

  /** typing: only hex digits, and only a full six-digit value changes the colour */
  onText( which: 'fg' | 'bg', value: string ): void {
    const clean = ( value || '' ).replace( /[^0-9a-f]/gi, '' ).substring( 0, 6 );
    if ( which === 'fg' ) {
      this.fgText = clean;
    } else {
      this.bgText = clean;
    }
    if ( clean.length === 6 ) {
      this.set( which, clean );
    }
  }

  /** a paste of "#1a2b3c" or "rgb..."-free hex lands whole */
  onBlur( which: 'fg' | 'bg' ): void {
    if ( which === 'fg' ) {
      this.fgText = this.fg.substring( 1 );
    } else {
      this.bgText = this.bg.substring( 1 );
    }
  }

  swap(): void {
    [ this.fg, this.bg ] = [ this.bg, this.fg ];
    this.fgText = this.fg.substring( 1 );
    this.bgText = this.bg.substring( 1 );
    this.measure();
    this.colorsChange.emit( { fg: this.fg, bg: this.bg } );
  }

  copyRatio(): void {
    navigator.clipboard?.writeText( `${ this.ratioLabel }:1` ).catch( () => undefined );
    this.copied = true;
    clearTimeout( this.copiedTimer );
    this.copiedTimer = setTimeout( () => ( this.copied = false ), 1200 );
  }

  get ratioLabel(): string {
    return this.ratio.toFixed( 2 ).replace( /\.?0+$/, '' );
  }

  /** the overall verdict above the checks */
  get verdict(): string {
    if ( this.ratio >= 7 ) {
      return 'Excellent';
    }
    if ( this.ratio >= 4.5 ) {
      return 'Good';
    }
    if ( this.ratio >= 3 ) {
      return 'Large Text Only';
    }
    return 'Poor';
  }

  private measure(): void {
    const ratio = this.colorService.contrastRatio( this.fg, this.bg );
    this.ratio = ratio;
    this.apca = this.colorService.apcaContrast( this.fg, this.bg );
    this.apcaLabel = this.colorService.apcaLevel( Math.abs( this.apca ) );
    this.checks = [
      { label: 'Normal Text', level: 'AA', need: 4.5, pass: ratio >= 4.5 },
      { label: 'Normal Text', level: 'AAA', need: 7, pass: ratio >= 7 },
      { label: 'Large Text', level: 'AA', need: 3, pass: ratio >= 3 },
      { label: 'Large Text', level: 'AAA', need: 4.5, pass: ratio >= 4.5 },
      { label: 'UI Components', level: 'AA', need: 3, pass: ratio >= 3 },
    ];
  }
}
